/**
 * Empty-slot reporter — lets a widget's shared onboarding/empty state collapse
 * its own grid cell.
 *
 * A dashboard widget's cell height is fixed by the RGL layout so the data-state
 * chart/list fits. When a widget has no data it renders {@link WidgetEmptyState}
 * (a short ~py-6 slot), which then sits top-aligned in that tall cell — a
 * nearly-empty tenant sees cards that are mostly whitespace. The empty slot
 * reports "I am empty" up to {@link DashboardGrid} via this context; the grid
 * shrinks that widget's cell height (see `collapseEmptyWidgets`) so
 * `compactType="vertical"` packs the neighbours upward. When data arrives the
 * empty slot unmounts and the cell returns to its authored height.
 *
 * No-op outside a DashboardGrid (the editor preview, a standalone widget, a
 * unit test): the report context defaults to null, so the hook does nothing.
 * See docs/platform/dashboard-widget-registry.md.
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react';

type ReportWidgetEmpty = (widgetKey: string, isEmpty: boolean) => void;

const WidgetEmptyReportContext = createContext<ReportWidgetEmpty | null>(null);
const WidgetKeyContext = createContext<string | null>(null);

/** DashboardGrid wraps its widgets in this to receive per-widget emptiness. */
export function WidgetEmptyReportProvider({
  report,
  children,
}: {
  report: ReportWidgetEmpty;
  children: ReactNode;
}) {
  return (
    <WidgetEmptyReportContext.Provider value={report}>
      {children}
    </WidgetEmptyReportContext.Provider>
  );
}

/** WidgetCard wraps each widget's body in this so its empty slot knows its key. */
export function WidgetKeyProvider({ widgetKey, children }: { widgetKey: string; children: ReactNode }) {
  return <WidgetKeyContext.Provider value={widgetKey}>{children}</WidgetKeyContext.Provider>;
}

/**
 * Called by {@link WidgetEmptyState}: while the empty slot is mounted, mark this
 * widget empty; clear it on unmount (data arrived, or the widget was removed).
 */
export function useReportWidgetEmpty(): void {
  const report = useContext(WidgetEmptyReportContext);
  const widgetKey = useContext(WidgetKeyContext);
  useEffect(() => {
    if (!report || !widgetKey) return;
    report(widgetKey, true);
    return () => report(widgetKey, false);
  }, [report, widgetKey]);
}
